import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Folder, FileText, X, ArrowLeft, Cloud } from 'lucide-react'
import type { OneDriveFile } from '@/types'
import { api } from '@/lib/api'

interface Props {
  onSelect: (file: OneDriveFile & { driveId: string }) => Promise<void>
  onClose: () => void
}

interface NavState {
  driveId?: string
  itemId?: string
  name: string
}

export default function OneDrivePicker({ onSelect, onClose }: Props) {
  const [nav, setNav] = useState<NavState[]>([{ name: 'OneDrive' }])
  const current = nav[nav.length - 1]
  const [selecting, setSelecting] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['onedrive-files', current.driveId, current.itemId],
    queryFn: () => api.onedrive.files(current.driveId, current.itemId),
  })

  async function handleSelect(file: OneDriveFile) {
    if (file.folder) {
      setNav(prev => [...prev, { driveId: file.parentReference?.driveId ?? data?.driveId, itemId: file.id, name: file.name }])
      return
    }
    setSelecting(file.id)
    try {
      await onSelect({ ...file, driveId: file.parentReference?.driveId ?? data?.driveId ?? '' })
      onClose()
    } finally {
      setSelecting(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl max-h-[80dvh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          {nav.length > 1 && (
            <button onClick={() => setNav(prev => prev.slice(0, -1))} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500">
              <ArrowLeft size={18} />
            </button>
          )}
          <Cloud size={16} className="text-brand-500" />
          <span className="flex-1 text-sm font-medium text-slate-800 truncate">
            {nav.map((n, i) => (
              <span key={i}>
                {i > 0 && <span className="text-slate-400 mx-1">/</span>}
                {n.name}
              </span>
            ))}
          </span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={18} />
          </button>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Loading…</div>
          )}
          {error && (
            <div className="p-4 text-sm text-red-600">{(error as Error).message ?? 'Failed to load files.'}</div>
          )}
          {data?.files.map(file => (
            <button
              key={file.id}
              onClick={() => handleSelect(file)}
              disabled={selecting === file.id}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 active:bg-slate-100 text-left border-b border-slate-50 last:border-0"
            >
              {file.folder
                ? <Folder size={18} className="text-amber-400 flex-shrink-0" />
                : <FileText size={18} className="text-slate-400 flex-shrink-0" />}
              <span className="flex-1 text-sm text-slate-800 truncate">{file.name}</span>
              {selecting === file.id && <span className="text-xs text-slate-400">Adding…</span>}
            </button>
          ))}
          {data?.files.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-8">Empty folder</p>
          )}
        </div>
      </div>
    </div>
  )
}
